import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@01_people/ui-library';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import CameraCaptureSC from './camera-capture.styled';
import { blobToFile } from '../utils';
import SvgIcon from '../../svg-icon';

import SvgCamera from '../../../assets/svg/camera-default.svg';
import Infotext from './components/info-text';
import RotationInfo from './components/rotation-info';
import { CameraCanvas, CameraViewFinder } from './components/camera-previews';

const cameraConstraints = {
  video: {
    width: {
      min: 1280,
      ideal: 1920,
      max: 2560
    },
    height: {
      min: 720,
      ideal: 1080,
      max: 1440
    },
    // to use rear camera primarly
    facingMode: 'environment'
  }
};

class CameraCapture extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isImageCaptured: false,
      imageTimestamp: ''
    };

    this.videoRef = React.createRef(null);
    this.playerRef = React.createRef(null);
  }

  componentDidMount() {
    this.initVideo();
  }

  componentDidUpdate(prevProps) {
    const { deviceOrientation } = this.props;
    if (deviceOrientation !== prevProps.deviceOrientation) {
      this.initVideo();
    }
  }

  componentWillUnmount() {
    if (
      !this.videoRef.current ||
      (!('srcObject' in this.videoRef.current) ||
        this.videoRef.current.srcObject === null)
    ) {
      return;
    }

    this.videoRef.current.srcObject.getTracks().forEach(track => track.stop());
  }

  initVideo = () => {
    function hasGetUserMedia() {
      return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    }

    if (hasGetUserMedia()) {
      const video = this.videoRef.current;

      navigator.mediaDevices
        .getUserMedia(cameraConstraints)
        .then(function(mediaStream) {
          video.srcObject = mediaStream;
        })
        .catch(function(err) {
          console.log(`${err.name}: ${err.message}`);
        });
    } else {
      console.log('getUserMedia not supported');
    }
  };

  cropCanvas = () => {
    const canvas = this.playerRef.current;
    const croppedPart = document.querySelector('.cropped-part');
    // creata temporary canvas to get cropped image
    const tmpCanvas = document.createElement('canvas');

    const canvasInfo = canvas.getBoundingClientRect();
    const croppedPartInfo = croppedPart.getBoundingClientRect();

    const canvasHeight = canvas.height;
    const canvasWidth = canvas.width;

    const xFactor = canvasWidth / canvasInfo.width;
    const yFactor = canvasHeight / canvasInfo.height;

    const cropX = croppedPartInfo.left - canvasInfo.left;
    const cropY = croppedPartInfo.top - canvasInfo.top;
    const cropWidth = croppedPartInfo.width;
    const cropHeight = croppedPartInfo.height;

    tmpCanvas.width = cropWidth;
    tmpCanvas.height = cropHeight;

    tmpCanvas
      .getContext('2d')
      .drawImage(
        canvas,
        cropX * xFactor,
        cropY * yFactor,
        cropWidth * xFactor,
        cropHeight * yFactor,
        0,
        0,
        cropWidth,
        cropHeight
      );

    return tmpCanvas;
  };

  getCurrentVideoFrameToCanvas = (video, canvas, scaleFactor = 1) => {
    const context = canvas.getContext('2d');
    const w = video.videoWidth * scaleFactor;
    const h = video.videoHeight * scaleFactor;
    canvas.width = w;
    canvas.height = h;
    context.drawImage(video, 0, 0, w, h);
  };

  handleCaptureClick = () => {
    const player = this.videoRef.current;
    const canvas = this.playerRef.current;

    this.getCurrentVideoFrameToCanvas(player, canvas);

    this.setState(state => ({
      ...state,
      isImageCaptured: true,
      imageTimestamp: new Date().toISOString()
    }));
  };

  handleAgainClick = () => {
    this.setState(state => ({
      ...state,
      isImageCaptured: false
    }));
  };

  handleSendClick = () => {
    const { imageTimestamp } = this.state;
    const { handleFile, closeModal } = this.props;
    const croppedCanvas = this.cropCanvas();
    croppedCanvas.toBlob(blob => {
      const file = blobToFile(blob, `Camera-${imageTimestamp}`);
      handleFile([file]);
      closeModal();
    });
  };

  render() {
    const { isImageCaptured } = this.state;
    const {
      responsiveContentView,
      deviceOrientation,
      mobileDevice,
      t
    } = this.props;

    if (deviceOrientation === 'landscape' && mobileDevice) {
      return <RotationInfo />;
    }

    return !responsiveContentView ? (
      <CameraCaptureSC>
        <div className="camera-wrapper">
          <CameraViewFinder
            ref={this.videoRef}
            isImageCaptured={isImageCaptured}
          />
          <CameraCanvas
            ref={this.playerRef}
            isImageCaptured={isImageCaptured}
          />

          <div className="flex">
            <Infotext
              title={t('camera.infoText.title')}
              text={t('camera.infoText.subTitle')}
            />

            <div className="cropped-part">
              <span />
            </div>

            {!isImageCaptured ? (
              <Button
                label={t('camera.captureButton')}
                onClick={this.handleCaptureClick}
              />
            ) : (
              <div>
                <Button
                  label={t('camera.newButton')}
                  styling="secondary"
                  onClick={this.handleAgainClick}
                />
                <Button
                  label={t('camera.sendButton')}
                  onClick={this.handleSendClick}
                />
              </div>
            )}
          </div>
        </div>
      </CameraCaptureSC>
    ) : (
      <CameraCaptureSC>
        <div className="camera-wrapper">
          <CameraViewFinder
            ref={this.videoRef}
            isImageCaptured={isImageCaptured}
          />
          <CameraCanvas
            ref={this.playerRef}
            isImageCaptured={isImageCaptured}
          />
          <div className="flex">
            <Infotext
              title={t('camera.infoText.title')}
              text={t('camera.infoText.subTitle')}
            />
            <div className="cropped-part">
              <span />
            </div>
            <div className="camera-buttons">
              {!isImageCaptured ? (
                <button
                  className="capture-btn"
                  onClick={this.handleCaptureClick}
                  type="button"
                >
                  <SvgIcon
                    icon={SvgCamera}
                    alt=""
                    customClass="camera-capture-svg"
                  />
                </button>
              ) : (
                <>
                  <Button
                    label={t('camera.newButton')}
                    styling="secondary"
                    onClick={this.handleAgainClick}
                    customClass="again-btn"
                  />
                  <Button
                    label={t('camera.sendButton')}
                    onClick={this.handleSendClick}
                    customClass="send-btn"
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </CameraCaptureSC>
    );
  }
}

CameraCapture.propTypes = {
  t: PropTypes.func.isRequired,
  deviceOrientation: PropTypes.string.isRequired,
  mobileDevice: PropTypes.bool.isRequired,
  handleFile: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  responsiveContentView: PropTypes.bool.isRequired
};

export default withTranslation('components')(
  connect(state => ({
    deviceOrientation: state.ui.orientation,
    mobileDevice: state.ui.isMobile,
    responsiveContentView: state.ui.responsiveContent
  }))(CameraCapture)
);
